/**
 * Project overview: the version history of the Blueprint, which is the audit surface —
 * versions are immutable, so "what exactly did we push" is always answerable
 * (01-architecture.md, data model rules).
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useBlueprints, useGenerateBlueprint, useJob, useProject } from '@/api/hooks'
import type { BlueprintSummary, PushState } from '@/api/types'
import { STATE_COPY } from '@/lib/pushState'
import { formatDateTime, formatNumber } from '@/lib/format'
import { Badge, Button, Cap, EmptyState, ErrorNotice, KeyValue, Panel, Spinner, StatTile, type Tone } from '@/components/ui'

const STATE_TONE: Record<PushState, Tone> = {
  draft: 'neutral',
  preflight: 'forge',
  blocked: 'danger',
  dry_run: 'forge',
  needs_fix: 'warn',
  queued_for_quota: 'warn',
  publishing_pages: 'forge',
  pushing: 'forge',
  reversing: 'forge',
  reversed: 'neutral',
  live_paused: 'ok',
  failed: 'danger',
}

function VersionRow({ projectId, summary, isLatest }: { projectId: string; summary: BlueprintSummary; isLatest: boolean }) {
  const state = summary.push_state
  return (
    <tr data-testid="version-row" className="border-b border-ink-800 last:border-b-0 hover:bg-ink-850/70">
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <Link
            to="/projects/$projectId/blueprints/$blueprintId"
            params={{ projectId, blueprintId: summary.id }}
            search={{ tab: summary.version > 1 ? ('changes' as const) : ('structure' as const) }}
            className="num text-[12.5px] font-semibold text-forge-300 underline decoration-dotted hover:text-forge-400"
          >
            v{summary.version}
          </Link>
          {isLatest ? <Badge tone="forge">latest</Badge> : null}
        </div>
        <div className="num mt-0.5 text-[10.5px] text-mist-500">{summary.id.slice(0, 8)}</div>
      </td>
      <td className="num px-3 py-2 text-[12px] text-mist-400">{formatDateTime(summary.created_at)}</td>
      <td className="num px-3 py-2 text-[12px] text-mist-300">
        {formatNumber(summary.campaign_count)} / {formatNumber(summary.ad_group_count)} /{' '}
        {formatNumber(summary.keyword_count)}
      </td>
      <td className="num px-3 py-2 text-[12px] text-mist-300">{formatNumber(summary.ad_count)}</td>
      <td className="px-3 py-2">
        <Badge tone={STATE_TONE[state]} title={STATE_COPY[state].detail}>
          {STATE_COPY[state].label}
        </Badge>
      </td>
      <td className="px-3 py-2">
        {summary.policy_lint_passed === null ? (
          <Badge tone="neutral">not recorded</Badge>
        ) : summary.policy_lint_passed ? (
          <Badge tone="ok">passed</Badge>
        ) : (
          <Badge tone="danger">violations</Badge>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <Link
          to="/projects/$projectId/blueprints/$blueprintId"
          params={{ projectId, blueprintId: summary.id }}
          search={{ tab: summary.version > 1 ? 'changes' : 'structure' }}
          className="text-[12px] text-mist-300 underline decoration-dotted hover:text-mist-100"
        >
          Review
        </Link>
      </td>
    </tr>
  )
}

export function ProjectScreen({ projectId }: { projectId: string }) {
  const project = useProject(projectId)
  const versions = useBlueprints(projectId)
  const generate = useGenerateBlueprint()
  const [jobId, setJobId] = useState<string | null>(null)
  const job = useJob(jobId)

  const ordered = [...(versions.data ?? [])].sort((a, b) => b.version - a.version)
  const latest = ordered[0]

  if (project.isLoading) return <Spinner label="Loading project…" />
  if (project.isError) return <ErrorNotice error={project.error} />

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Cap>Project</Cap>
          <h1 className="mt-1 text-[22px] leading-8 font-semibold tracking-tight text-mist-100">
            {project.data?.name}
          </h1>
          <p className="num mt-0.5 text-[12.5px] text-mist-400">{project.data?.site_url}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={project.data?.status === 'ready' ? 'ok' : 'forge'}>{project.data?.status}</Badge>
          <Button
            data-testid="generate-version"
            disabled={generate.isPending || (jobId !== null && job.data?.status !== 'succeeded')}
            onClick={async () => {
              const accepted = await generate.mutateAsync({
                projectId,
                body: { include_ad_copy: true, include_landing_pages: true },
              })
              setJobId(accepted.job_id)
              void versions.refetch()
            }}
          >
            {generate.isPending ? 'Queueing…' : 'Generate a new version'}
          </Button>
        </div>
      </div>

      {jobId && job.data && job.data.status !== 'succeeded' ? (
        <Panel testId="project-job">
          <div className="flex items-center gap-3">
            <Spinner label={`${job.data.kind}: ${job.data.message ?? 'working'}`} />
            <span className="num ml-auto text-[12px] text-mist-400">
              {Math.round(job.data.progress * 100)}%
            </span>
          </div>
        </Panel>
      ) : null}
      {generate.isError ? <ErrorNotice error={generate.error} /> : null}

      {latest ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <StatTile label="Blueprint versions" value={formatNumber(ordered.length)} testId="stat-versions" />
          <StatTile label="Campaigns" value={formatNumber(latest.campaign_count)} />
          <StatTile label="Ad groups" value={formatNumber(latest.ad_group_count)} testId="stat-adgroups" />
          <StatTile label="Keywords" value={formatNumber(latest.keyword_count)} testId="stat-keywords" />
          <StatTile label="Ads" value={formatNumber(latest.ad_count)} />
        </div>
      ) : null}

      <Panel
        title="Blueprint versions"
        subtitle="Immutable. Editing creates version n+1, which is what makes the diff and the audit trail possible."
        actions={versions.isFetching ? <span className="text-[11.5px] text-mist-500">refreshing…</span> : null}
        testId="versions-panel"
      >
        {versions.isLoading ? (
          <Spinner label="Loading versions…" />
        ) : ordered.length === 0 ? (
          <EmptyState
            title="No Blueprint yet"
            body="Ingest the site and generate a structure — the first version appears here."
            action={
              <Link to="/" className="text-[12.5px] text-forge-300 underline decoration-dotted">
                Start an ingest
              </Link>
            }
          />
        ) : (
          <div className="-mx-4 overflow-x-auto px-1">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr className="border-b border-ink-700">
                  {['Version', 'Created', 'Camp / groups / keywords', 'Ads', 'Push state', 'Policy lint', ''].map(
                    (heading) => (
                      <th key={heading} scope="col" className="label-cap px-3 py-2 text-left">
                        {heading}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {ordered.map((summary) => (
                  <VersionRow
                    key={summary.id}
                    projectId={projectId}
                    summary={summary}
                    isLatest={summary.id === latest?.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Project facts" testId="project-facts">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KeyValue label="Project id">
            <span className="num text-[12px]">{project.data?.id}</span>
          </KeyValue>
          <KeyValue label="Slug">
            <span className="num text-[12px]">{project.data?.slug}</span>
          </KeyValue>
          <KeyValue label="Created">
            <span className="num text-[12px]">{formatDateTime(project.data?.created_at)}</span>
          </KeyValue>
          <KeyValue label="Latest push state">
            {project.data?.latest_push_state ? (
              <Badge tone={STATE_TONE[project.data.latest_push_state]}>
                {STATE_COPY[project.data.latest_push_state].label}
              </Badge>
            ) : (
              <span className="text-[12px] text-mist-500">never pushed</span>
            )}
          </KeyValue>
        </div>
      </Panel>
    </div>
  )
}
